import { FC, memo, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Button, IconButton, Stack, Typography } from '@mui/material';
import { Brightness4 as BrightnessIcon, Menu as MenuIcon, Timeline as TimelineIcon } from '@mui/icons-material';

import { App } from '@playq/octopus2-apps';

import { IThemeModeContext, SyntheticEventWithMetaKey } from '/common/models';
import { ThemeModeContext } from '/common/theme';
import { appToolkit, authToolkit, IUIState, kbarToolkit, uiToolkit, useAppDispatch } from '/store';
import { AppPicker } from '/component/Apps/AppsPicker';
import { AppIcon } from '/component/Apps/AppIcon';
import { ProgressIndicator } from '/common/ProgressIndicator';
import { OnlinePageUsers } from '/shared/OnlinePageUsers';
import { OnlineDevices } from '/shared/OnlineDevices';
import { InvokeAllScriptsButton } from '/component/Services/Scripts/InvokeAllScriptsButton';
import { navigateToURL } from '/helpers';
import { fetchUserSettings } from '/store/toolkits/ui/actions';

import { Notification } from './Notification';
import { SpacePicker } from './SpacePicker';
import { Corporate } from './Corporate';
import { Profile } from './Profile';
import {
  AppIconWrapper,
  AppInfo,
  AppName,
  headerHeight,
  LinkToHome,
  StyledDivider,
  StyledNav,
  StyledToolbar,
} from './styles';

export const Header: FC = memo(() => {
  const { currentThemeMode, updateThemeMode }: IThemeModeContext = useContext(ThemeModeContext);

  const [appSelectOpen, setAppSelectOpen] = useState(false);

  const isAuthorizationSet: boolean = useSelector(authToolkit.selectors.isServicesAuthorizationSet);
  const app: App | undefined = useSelector(appToolkit.selectors.app);
  const uiState: IUIState = useSelector(uiToolkit.selectors.state);
  const openSelectAppModal = useSelector(kbarToolkit.selectors.openSelectAppModal);

  const dispatch = useDispatch();
  const updateUIState = useCallback((newState: IUIState) => dispatch(uiToolkit.actions.update(newState)), [dispatch]);
  const appDispatch = useAppDispatch();

  useEffect(() => {
    appDispatch(fetchUserSettings());
    // one-time action
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAppSelectOpen = useCallback(
    (val: boolean) => () => {
      setAppSelectOpen(val);
      if (!val) {
        dispatch(kbarToolkit.actions.setOpenSelectAppModal(false));
      }
    },
    [dispatch]
  );

  useEffect(
    function handleSelectAppModalByKbar() {
      if (openSelectAppModal) {
        setAppSelectOpen(true);
      }
    },
    [dispatch, handleAppSelectOpen, openSelectAppModal]
  );

  const toggleNavSidebar = () => {
    updateUIState({
      ...uiState,
      navSidebarOpen: !uiState.navSidebarOpen,
    });
  };

  const toggleTimeline = () => {
    const timelineState = uiState.timeline;
    const newOpen = !timelineState.open;
    updateUIState({
      ...uiState,
      timeline: { ...timelineState, filterBy: {}, open: newOpen },
    });
  };

  const updateThemeModeHandler = () => {
    if (currentThemeMode === 'dark') {
      updateThemeMode('light');
    } else {
      updateThemeMode('dark');
    }
  };

  const homeURL = '/';

  const handleGoHome = (event: SyntheticEventWithMetaKey) => navigateToURL({ url: homeURL, event });

  return (
    <>
      <AppBar
        position='fixed'
        sx={[
          (theme) => ({
            height: headerHeight,
            zIndex: theme.zIndex.drawer + 1,
          }),
        ]}
        enableColorOnDark={true}
      >
        <StyledToolbar variant='dense' disableGutters>
          <AppInfo>
            <ProgressIndicator />
            <IconButton onClick={toggleNavSidebar} color='inherit' aria-label='Toggle nav sidebar' size='large'>
              <MenuIcon />
            </IconButton>
            <Stack spacing={1} direction='row' alignItems='center'>
              <Typography noWrap={true} className='title' variant='h6'>
                <LinkToHome onClick={handleGoHome} underline='none' href={homeURL}>
                  PlayQ Octopus
                </LinkToHome>
              </Typography>
              <OnlinePageUsers />
              <OnlineDevices />
            </Stack>
          </AppInfo>
          <StyledNav>
            <Button
              onClick={handleAppSelectOpen(true)}
              startIcon={
                app ? (
                  <AppIconWrapper>
                    <AppIcon app={app} />
                  </AppIconWrapper>
                ) : null
              }
              color='inherit'
              aria-label='Select App'
              data-testid='select-app'
            >
              <AppName noWrap={true} color='inherit'>
                {app ? app.name : 'Select App'}
              </AppName>
            </Button>
            <SpacePicker />
            <StyledDivider orientation='vertical' />
            {app && (
              <>
                {isAuthorizationSet && <InvokeAllScriptsButton />}

                <IconButton onClick={toggleTimeline} color='inherit' aria-label='Toggle timeline' size='large'>
                  <TimelineIcon />
                </IconButton>
              </>
            )}
            <Notification />
            <IconButton onClick={updateThemeModeHandler} color='inherit' aria-label='Select Theme' size='large'>
              <BrightnessIcon />
            </IconButton>
            <Corporate />
            <Profile />
          </StyledNav>
        </StyledToolbar>
      </AppBar>
      {appSelectOpen && <AppPicker onClose={handleAppSelectOpen(false)} />}
    </>
  );
});
